import { faPlus, faTable, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useState } from "react";
import { MultipleTableAnswerValue } from "consumerhouse-shared";
import { MultipleTableQuestionOptions } from "consumerhouse-shared";
import { RawFormCheck, RawFormRadio, SmallInput } from "../FormElements";
import { GroupContainer } from "../GroupContainer";
import { LinkButton } from "../LinkButton";
import { OptionsEditProps, QuestionContainer, QuestionProps, QuestionNote, QuestionDefinition } from "../QuestionAnswer";
import styled from "styled-components";
import { useLanguage } from "../../useLanguage";
import { TableContainer, Table, TableHead, TableRow, TableHeader, TableBody, TableData } from "../TableElements";
import { IconButton } from "../../IconButton";
import Button from "../Button";
import { arrayEqual } from "../../util";

export const MultipleTableQuestionDefinition: QuestionDefinition<MultipleTableQuestionOptions> = {
    icon: faTable,
    defaultOptions: {
        multiple: false,
        columns: ["kolom 1", "kolom 2"],
        rows: ["rij 1", "rij 2"],
    },
    questionComponent: MultipleTableQuestion,
    optionsEditComponent: MultipleTableOptionsEdit,
};

function MultipleTableQuestion(props: QuestionProps<MultipleTableQuestionOptions, MultipleTableAnswerValue>) {
    const options = props.question.optionsJson!;
    const [selected, setSelected] = useState(() => props.value?.selected ?? options.rows.map(() => [] as string[]));
    const language = useLanguage();

    const Input = options.multiple ? RawFormCheck : RawFormRadio;

    function handleChange(row: number, checked: boolean, value: string) {
        const newSelected = [...selected];
        if (options.multiple) {
            if (checked) newSelected[row].push(value);
            else newSelected[row].splice(newSelected[row].indexOf(value), 1);
        } else {
            newSelected[row] = [value];
        }
        setSelected(newSelected);
        if (props.question.optional || newSelected.every((arr) => arr.length)) {
            props.onChange({
                selected: newSelected,
            });
        }
    }

    return (
        <>
            {options.multiple && <QuestionNote>{language("question-multiple")}</QuestionNote>}
            <QuestionContainer>
                <TableContainer>
                    {!options.mirror ? (
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableHeader></TableHeader>
                                    {options.columns.map((e) => (
                                        <TableHeader key={e} style={{ maxWidth: "150px", minWidth: "100px", whiteSpace: "pre-wrap" }}>
                                            {e}
                                        </TableHeader>
                                    ))}
                                    {options.noneOption && (
                                        <TableHeader style={{ maxWidth: "150px", minWidth: "100px", whiteSpace: "pre-wrap" }}>
                                            {language("question-none-table")}
                                        </TableHeader>
                                    )}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {options.rows.map((e, row) => (
                                    <TableRow key={e}>
                                        <TableData style={{ maxWidth: "500px", whiteSpace: "pre-wrap" }}>{e}</TableData>
                                        {options.columns.map((column) => (
                                            <TableData key={column}>
                                                <Input
                                                    disabled={props.readOnly}
                                                    checked={selected[row]?.some((e) => e === column) ?? false}
                                                    onChange={(ev) => handleChange(row, ev.target.checked, column)}>
                                                    {/* {options.columns[col]} */}
                                                </Input>
                                            </TableData>
                                        ))}
                                        {options.noneOption && (
                                            <TableData>
                                                <Input
                                                    disabled={props.readOnly}
                                                    checked={arrayEqual(selected[row], ["none"])}
                                                    onChange={(ev) => {
                                                        if (ev.target.checked) {
                                                            let newSelected = [...selected];
                                                            newSelected[row] = ["none"];
                                                            setSelected(newSelected);
                                                            if (props.question.optional || newSelected.every((arr) => arr.length)) {
                                                                props.onChange({
                                                                    selected: newSelected,
                                                                });
                                                            }
                                                        }
                                                    }}></Input>
                                            </TableData>
                                        )}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    ) : (
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableData></TableData>
                                    {options.rows.map((e) => (
                                        <TableData key={e} style={{ maxWidth: "150px", minWidth: "100px", whiteSpace: "pre-wrap" }}>
                                            {e}
                                        </TableData>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {options.columns.map((row, rowIndex) => (
                                    <TableRow key={row}>
                                        <TableHeader style={{ maxWidth: "500px", whiteSpace: "pre-wrap" }}>{row}</TableHeader>
                                        {options.rows.map((column, columnIndex) => (
                                            <TableData key={column}>
                                                <Input
                                                    disabled={props.readOnly}
                                                    checked={selected[columnIndex]?.some((e) => e === row) ?? false}
                                                    onChange={(ev) => handleChange(columnIndex, ev.target.checked, row)}>
                                                    {/* {options.columns[col]} */}
                                                </Input>
                                            </TableData>
                                        ))}
                                    </TableRow>
                                ))}
                                {options.noneOption && (
                                    <TableRow>
                                        <TableHeader style={{ maxWidth: "150px", minWidth: "100px", whiteSpace: "pre-wrap" }}>
                                            {language("question-none-table")}
                                        </TableHeader>
                                        {options.rows.map((row, rowIndex) => (
                                            <TableData key={row}>
                                                <Input
                                                    disabled={props.readOnly}
                                                    checked={arrayEqual(selected[rowIndex], ["none"])}
                                                    onChange={(ev) => {
                                                        if (ev.target.checked) {
                                                            let newSelected = [...selected];
                                                            newSelected[rowIndex] = ["none"];
                                                            setSelected(newSelected);
                                                            if (props.question.optional || newSelected.every((arr) => arr.length)) {
                                                                props.onChange({
                                                                    selected: newSelected,
                                                                });
                                                            }
                                                        }
                                                    }}></Input>
                                            </TableData>
                                        ))}
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    )}
                </TableContainer>
            </QuestionContainer>
        </>
    );
}

function MultipleTableOptionsEdit(props: OptionsEditProps<MultipleTableQuestionOptions>) {
    const options = props.options ?? { multiple: false, columns: ["Kolom1", "Kolom2"], rows: ["Rij1", "Rij2"] };

    function removeColumn(column: number) {
        const newColumns = [...options.columns];
        newColumns.splice(column, 1);
        props.onChange({
            ...options,
            columns: newColumns,
        });
    }

    function removeRow(row: number) {
        const newRows = [...options.rows];
        newRows.splice(row, 1);
        props.onChange({
            ...options,
            rows: newRows,
        });
    }

    function addColumn() {
        props.onChange({
            ...options,
            columns: [...options.columns, ""],
        });
    }

    function addRow() {
        props.onChange({
            ...options,
            rows: [...options.rows, ""],
        });
    }

    function setRow(row: number, value: string) {
        const newRows = [...options.rows];
        newRows[row] = value;
        props.onChange({
            ...options,
            rows: newRows,
        });
    }

    function setColumn(column: number, value: string) {
        const newColumns = [...options.columns];
        newColumns[column] = value;
        props.onChange({
            ...options,
            columns: newColumns,
        });
    }

    return (
        <GroupContainer title="Tabel opties" color="#5a8ee8">
            <TableContainer>
                {!options.mirror ? (
                    <Table>
                        <TableBody>
                            <TableRow>
                                <TableHeader></TableHeader>
                                {options.columns.map((column, columnIndex) => (
                                    <TableHeader key={columnIndex}>
                                        <SmallInput
                                            placeholder="kolom beschrijving"
                                            value={column}
                                            onChange={(ev) => setColumn(columnIndex, ev.target.value)}
                                        />
                                        <IconButton type="button" onClick={() => removeColumn(columnIndex)}>
                                            <FontAwesomeIcon icon={faTrash} />
                                        </IconButton>
                                    </TableHeader>
                                ))}
                            </TableRow>
                            {options.rows.map((row, rowIndex) => (
                                <TableRow key={rowIndex}>
                                    <TableHeader>
                                        <SmallInput placeholder="rij beschrijving" value={row} onChange={(ev) => setRow(rowIndex, ev.target.value)} />
                                        <IconButton type="button" onClick={() => removeRow(rowIndex)}>
                                            <FontAwesomeIcon icon={faTrash} />
                                        </IconButton>
                                    </TableHeader>
                                    {options.columns.map((e, i) => (
                                        <TableData key={i}>{options.multiple ? <RawFormCheck disabled /> : <RawFormRadio disabled />}</TableData>
                                    ))}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                ) : (
                    <Table>
                        <TableBody>
                            <TableRow>
                                <TableHeader></TableHeader>
                                {options.rows.map((row, rowIndex) => (
                                    <TableHeader key={rowIndex}>
                                        <SmallInput
                                            placeholder="kolom beschrijving"
                                            value={row}
                                            onChange={(ev) => setRow(rowIndex, ev.target.value)}
                                        />
                                        <IconButton type="button" onClick={() => removeRow(rowIndex)}>
                                            <FontAwesomeIcon icon={faTrash} />
                                        </IconButton>
                                    </TableHeader>
                                ))}
                            </TableRow>
                            {options.columns.map((column, columnIndex) => (
                                <TableRow key={columnIndex}>
                                    <TableHeader>
                                        <SmallInput
                                            placeholder="rij beschrijving"
                                            value={column}
                                            onChange={(ev) => setColumn(columnIndex, ev.target.value)}
                                        />
                                        <IconButton type="button" onClick={() => removeColumn(columnIndex)}>
                                            <FontAwesomeIcon icon={faTrash} />
                                        </IconButton>
                                    </TableHeader>
                                    {options.rows.map((e, i) => (
                                        <TableData key={i}>{options.multiple ? <RawFormCheck disabled /> : <RawFormRadio disabled />}</TableData>
                                    ))}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </TableContainer>
            <div style={{ display: "flex", alignItems: "center", margin: "0.8em 0 0 0" }}>
                <Button type="button" style={{ margin: "0 0.4em" }} icon={faPlus} onClick={addColumn}>
                    Kolum
                </Button>
                <Button type="button" style={{ margin: "0 0.4em" }} icon={faPlus} onClick={addRow}>
                    Rij
                </Button>
                <RawFormCheck checked={options.multiple} onChange={(ev) => props.onChange({ ...options, multiple: ev.target.checked })}>
                    Meerdere
                </RawFormCheck>
                <RawFormCheck checked={options.mirror} onChange={(ev) => props.onChange({ ...options, mirror: ev.target.checked })}>
                    Gespiegeld
                </RawFormCheck>
                <RawFormCheck checked={options.coded} onChange={(ev) => props.onChange({ ...options, coded: ev.target.checked })}>
                    Gecodeerde kolom
                </RawFormCheck>
                <RawFormCheck checked={options.noneOption} onChange={(ev) => props.onChange({ ...options, noneOption: ev.target.checked })}>
                    'Geen' kolom
                </RawFormCheck>
            </div>
        </GroupContainer>
    );
}
